import { NavLink } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { hasCreativeStudioAccess } from '@/config/featureAccess';

const baseLinkClasses =
  'flex items-center space-x-3 px-2.5 py-3 rounded-lg transition-all duration-200 ease-out font-medium whitespace-nowrap';

const linkClassName = ({ isActive }: { isActive: boolean }) =>
  `${baseLinkClasses} ${
    isActive
      ? 'bg-flouri-darkGray text-white ring-inset ring-white/20 ' +
        'ring-[2.5px] group-hover:ring-[1.5px] group-focus-within:ring-[1.5px] ' +
        'shadow-[inset_-2.5px_-2.5px_5px_rgb(0_0_0_/_0.47)] ' +
        'group-hover:shadow-[inset_2.5px_2.5px_5px_rgb(255_255_255_/_0.27),inset_-2.5px_-2.5px_5px_rgb(0_0_0_/_0.47)] ' +
        'group-focus-within:shadow-[inset_2.5px_2.5px_5px_rgb(255_255_255_/_0.27),inset_-2.5px_-2.5px_5px_rgb(0_0_0_/_0.47)]'
      : 'text-white hover:bg-white/5 hover:ring-[1.5px] hover:ring-inset hover:ring-white/20 ' +
        'hover:shadow-[inset_2px_2px_4px_rgb(0_0_0_/_0.38),inset_-2px_-2px_4px_rgb(255_255_255_/_0.2)]'
  }`;

const labelClasses =
  'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 delay-75 ' +
  'text-[19px] font-bold ' +
  '[text-shadow:_0_1px_2px_rgb(0_0_0_/_0.5)]';

export function Sidebar() {
  const memberUuid = useAuthStore((state) => state.memberUuid);

  return (
    <>
      <div aria-hidden="true" className="hidden md:block w-16 shrink-0" />
      <aside
        aria-label="Primary"
        className="hidden md:block fixed top-[var(--header-h)] left-0 w-16 h-[calc(100vh_-_var(--header-h))] bg-white z-40"
      >
        <div className="group absolute inset-y-0 left-0 w-16 hover:w-56 focus-within:w-56 bg-flouri-darkGray hover:bg-black/[0.75] focus-within:bg-black/[0.75] hover:backdrop-blur-sm focus-within:backdrop-blur-sm hover:backdrop-saturate-150 focus-within:backdrop-saturate-150 border-r border-gray-800 hover:border-white/40 focus-within:border-white/40 hover:shadow-[inset_-1px_0_0_rgb(255_255_255_/_0.18),0_4px_24px_rgb(0_0_0_/_0.2)] focus-within:shadow-[inset_-1px_0_0_rgb(255_255_255_/_0.18),0_4px_24px_rgb(0_0_0_/_0.2)] transition-all duration-200 ease-out overflow-hidden z-30 flex flex-col">
          <nav className="px-3 py-4 space-y-1 flex-1">
            <NavLink to="/dashboard" end className={linkClassName}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span className={labelClasses}>Dashboard</span>
            </NavLink>
            {hasCreativeStudioAccess(memberUuid) && (
              <NavLink to="/creative-studio" className={linkClassName}>
                <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                <span className={labelClasses}>Creative Studio</span>
              </NavLink>
            )}
            <NavLink to="/media-library" className={linkClassName}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className={labelClasses}>Media Library</span>
            </NavLink>
            <NavLink to="/account" className={linkClassName}>
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span className={labelClasses}>Campaigns</span>
            </NavLink>
          </nav>
          <div className="w-56 px-3 pb-4 pt-2 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-200 delay-75">
            <p className="text-xs text-white/80 leading-snug text-center">
              Need help? Please contact{' '}
              <a
                href="mailto:support@flouri.ai"
                className="text-white font-semibold underline decoration-white/40 hover:decoration-white"
              >
                support@flouri.ai
              </a>
              .
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}
